import React from 'react';

// ─────────────────────────────────────────────────────────────────────────────
//  Light UI — shared palette, primitives, and global stylesheet.
//
//  Mounting <LightGlobalStyles /> once at the app root installs the
//  --lp-* CSS variables + keyframes + base classes used by every screen.
//  The individual screens render JSX using these classes (no Tailwind paper
//  palette is needed — everything paper/sage/ink-tinted lives in here).
// ─────────────────────────────────────────────────────────────────────────────

export const LIGHT_PALETTE = {
  paper: '#eeefe8',
  paper2: '#f6f5ee',
  paperWarm: '#faf9f3',
  ink: '#1a1f1a',
  inkSoft: '#4a524a',
  inkMute: '#8a8f86',
  line: 'rgba(40,50,40,0.10)',
  violet: '#8b5cf6',
  pink: '#ec4899',
  orange: '#f97316',
  amber: '#f59e0b',
  mint: '#14b8a6',
  serif: "'Fraunces', 'Times New Roman', serif",
  mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
} as const;

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ─────────────────────────────────────────────────────────────────────────────
//  Global stylesheet — installed once at the root.
// ─────────────────────────────────────────────────────────────────────────────

export function LightGlobalStyles() {
  return (
    <style>{`
:root {
  --lp-paper: #eeefe8;
  --lp-paper-2: #f6f5ee;
  --lp-paper-warm: #faf9f3;
  --lp-ink: #1a1f1a;
  --lp-ink-deep: #3a4339;
  --lp-ink-soft: #4a524a;
  --lp-ink-mute: #8a8f86;
  --lp-line: rgba(40,50,40,0.10);
  --lp-line-strong: rgba(40,50,40,0.18);

  --lp-violet: #8b5cf6;
  --lp-pink:   #ec4899;
  --lp-orange: #f97316;
  --lp-amber:  #f59e0b;
  --lp-mint:   #14b8a6;

  --lp-serif: 'Newsreader', 'Times New Roman', serif;
  --lp-mono: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
}

html, body, #root {
  background: var(--lp-paper);
}

@keyframes lp-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes lp-pulse {
  0%, 100% { opacity: 0.55; transform: scale(0.95); }
  50%      { opacity: 1.0;  transform: scale(1.10); }
}
@keyframes lp-ring-rotate { to { transform: rotate(360deg); } }
@keyframes lp-ring-breathe {
  0%, 100% { opacity: 0.85; transform: scale(1.00); }
  50%      { opacity: 1.00; transform: scale(1.02); }
}
@keyframes lp-avatar-pulse {
  0%, 100% { transform: scale(1.00); }
  50%      { transform: scale(1.045); }
}
@keyframes lp-rings-drift {
  0%, 100% { transform: translate(0, 0); }
  50%      { transform: translate(8px, -6px); }
}
/* Earned-credits "+N" badge — pops in next to the balance so the reward
   registers even without the (now removed) end-of-interview modal. */
@keyframes lp-credits-pop {
  0%   { opacity: 0; transform: scale(0.4); }
  55%  { opacity: 1; transform: scale(1.35); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes lp-spin { to { transform: rotate(360deg); } }
/* The interviewer's rotating "thinking" phrase — each new phrase eases in. */
@keyframes lp-phrase-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.lp-thinking-phrase {
  display: inline-block;
  animation: lp-phrase-in 420ms ease both;
}
@media (prefers-reduced-motion: reduce) {
  .lp-thinking-phrase { animation: none; }
}
@keyframes lp-spin-reverse { to { transform: rotate(-360deg); } }
@keyframes lp-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* ───── Shell ───────────────────────────────────────────────────────────── */
.lp-root {
  position: fixed;
  inset: 0;
  background: var(--lp-paper);
  color: var(--lp-ink);
  overflow: hidden;
  font-family: var(--lp-mono);
  /* Keep subpixel antialiasing — antialiased/grayscale makes Fraunces
     serif strokes look ghosted at body sizes. Weights below are tuned
     for subpixel rendering. */
  -webkit-font-smoothing: auto;
  -moz-osx-font-smoothing: auto;
}
.lp-scroll-root {
  position: fixed;
  inset: 0;
  background: var(--lp-paper);
  color: var(--lp-ink);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  font-family: var(--lp-mono);
}

.lp-grain {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.32;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 320 320' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.6' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.08 0 0 0 0 0.09 0 0 0 0 0.08 0 0 0 0.18 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}
.lp-rings {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.55;
  background-image:
    radial-gradient(circle 360px at 92% 14%, transparent 356px, rgba(40,50,40,0.06) 357px, rgba(40,50,40,0.06) 358px, transparent 359px),
    radial-gradient(circle 260px at 92% 14%, transparent 256px, rgba(40,50,40,0.05) 257px, rgba(40,50,40,0.05) 258px, transparent 259px),
    radial-gradient(circle 170px at 92% 14%, transparent 166px, rgba(40,50,40,0.04) 167px, rgba(40,50,40,0.04) 168px, transparent 169px),
    radial-gradient(circle 320px at 8% 86%, transparent 316px, rgba(139,92,246,0.06) 317px, rgba(139,92,246,0.06) 318px, transparent 319px),
    radial-gradient(circle 220px at 8% 86%, transparent 216px, rgba(236,72,153,0.05) 217px, rgba(236,72,153,0.05) 218px, transparent 219px);
  animation: lp-rings-drift 24s ease-in-out infinite;
}

/* ───── Typography helpers ──────────────────────────────────────────────── */
.lp-serif { font-family: var(--lp-serif); font-optical-sizing: auto; }
.lp-mono { font-family: var(--lp-mono); }
.lp-eyebrow-text {
  font-family: var(--lp-mono);
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--lp-ink-mute);
  font-weight: 600;
}

/* ───── Card / panel primitives ─────────────────────────────────────────── */
.lp-card {
  background: var(--lp-paper-2);
  border: 1px solid var(--lp-line);
  border-radius: 2px 22px 2px 22px;
  box-shadow:
    0 1px 0 rgba(255,255,255,0.85) inset,
    0 24px 60px -16px rgba(40,50,40,0.22),
    0 8px 24px -10px rgba(40,50,40,0.10);
  position: relative;
}
.lp-card-tight {
  border-radius: 2px 16px 2px 16px;
  box-shadow:
    0 1px 0 rgba(255,255,255,0.8) inset,
    0 18px 40px -16px rgba(40,50,40,0.18);
}
.lp-frost-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 2px 14px 2px 14px;
  background: rgba(255,255,255,0.7);
  border: 1px solid var(--lp-line);
  font-family: var(--lp-mono);
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--lp-ink);
  font-weight: 600;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 1px 0 rgba(255,255,255,0.8) inset, 0 4px 12px -6px rgba(40,50,40,0.10);
}

/* ───── Buttons ────────────────────────────────────────────────────────── */
.lp-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 18px;
  border-radius: 2px 14px 2px 14px;
  border: 1px solid var(--lp-line-strong);
  background: var(--lp-paper-warm);
  color: var(--lp-ink);
  font-family: var(--lp-mono);
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 600;
  cursor: pointer;
  transition: background 180ms ease, transform 180ms ease, border-color 180ms ease, color 180ms ease, box-shadow 180ms ease;
  box-shadow: 0 1px 0 rgba(255,255,255,0.8) inset, 0 4px 12px -6px rgba(40,50,40,0.10);
}
.lp-btn:hover:not(:disabled) {
  background: linear-gradient(rgba(255,255,255,0.96), rgba(255,255,255,0.96)) padding-box,
              linear-gradient(135deg, var(--lp-violet) 0%, var(--lp-pink) 50%, var(--lp-orange) 100%) border-box;
  border: 1px solid transparent;
}
.lp-btn:active:not(:disabled) { transform: scale(0.97); }
.lp-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.lp-btn-primary {
  background:
    linear-gradient(rgba(255,255,255,0.92), rgba(250,249,243,0.92)) padding-box,
    conic-gradient(from 120deg, var(--lp-violet), var(--lp-pink), var(--lp-orange), var(--lp-amber), var(--lp-violet)) border-box;
  color: var(--lp-ink);
  border: 2px solid transparent;
  box-shadow:
    0 1px 0 rgba(255,255,255,0.85) inset,
    0 0 0 1px rgba(236,72,153,0.08),
    0 10px 26px -12px rgba(236,72,153,0.35);
}
.lp-btn-primary:hover:not(:disabled) {
  background:
    linear-gradient(rgba(255,255,255,0.98), rgba(250,249,243,0.98)) padding-box,
    conic-gradient(from 60deg, var(--lp-pink), var(--lp-orange), var(--lp-amber), var(--lp-violet), var(--lp-pink)) border-box;
  border: 2px solid transparent;
  color: var(--lp-ink);
  box-shadow:
    0 1px 0 rgba(255,255,255,0.90) inset,
    0 0 0 1px rgba(249,115,22,0.18),
    0 12px 30px -10px rgba(249,115,22,0.35);
}

.lp-btn-ghost {
  background: transparent;
  border: 1px dashed var(--lp-line-strong);
  color: var(--lp-ink-soft);
  box-shadow: none;
}
.lp-btn-ghost:hover:not(:disabled) {
  background: rgba(255,255,255,0.55);
  color: var(--lp-ink);
  border-style: solid;
}

.lp-btn-link {
  background: transparent;
  border: 0;
  color: var(--lp-ink-soft);
  padding: 0;
  font-family: var(--lp-mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  transition: color 160ms ease;
}
.lp-btn-link:hover:not(:disabled) { color: var(--lp-ink); }
.lp-btn-link:disabled { opacity: 0.4; cursor: not-allowed; }

.lp-icon-btn {
  width: 38px; height: 38px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 2px 12px 2px 12px;
  background: rgba(255,255,255,0.7);
  border: 1px solid var(--lp-line);
  color: var(--lp-ink);
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 1px 0 rgba(255,255,255,0.8) inset, 0 4px 12px -6px rgba(40,50,40,0.10);
  transition: background 180ms ease, transform 180ms ease, border-color 180ms ease;
}
.lp-icon-btn:hover:not(:disabled) {
  background: linear-gradient(rgba(255,255,255,0.95), rgba(255,255,255,0.95)) padding-box,
              linear-gradient(135deg, var(--lp-violet) 0%, var(--lp-pink) 50%, var(--lp-orange) 100%) border-box;
  border: 1px solid transparent;
}
.lp-icon-btn:active:not(:disabled) { transform: scale(0.96); }
.lp-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* ───── Train top bar ──────────────────────────────────────────────────────
   Scoped sizing so the identity chip, credits chip, and icon buttons share
   one uniform baseline. The shared .lp-frost-chip / .lp-icon-btn primitives
   are used across many screens, so keep these overrides local to the header. */
.lp-topbar-chip {
  height: 32px;
  padding: 0 12px;
  letter-spacing: 0.14em;
  border-radius: 2px 12px 2px 12px;
}
/* With the credits chip showing, the header wrapped to a second row on most
   phones, taking 40px from the interviewer's message. Tighten the chips just
   enough to keep one row down to 375px wide. */
@media (max-width: 440px) {
  .lp-topbar-chip { padding: 0 10px; letter-spacing: 0.1em; }
}
@media (max-width: 380px) {
  .lp-topbar-chip { padding: 0 8px; letter-spacing: 0.06em; }
}
.lp-topbar-icon {
  width: 32px; height: 32px;
  border-radius: 2px 12px 2px 12px;
}
/* Credits — warm amber accent so the live balance reads at a glance while
   staying inside the paper palette. */
.lp-credits-chip {
  color: #5a3a05;
  border: 1px solid rgba(245,158,11,0.55);
  background:
    linear-gradient(rgba(255,255,255,0.5), rgba(255,255,255,0.5)),
    linear-gradient(135deg, rgba(245,158,11,0.22), rgba(249,115,22,0.18));
  box-shadow: 0 1px 0 rgba(255,255,255,0.8) inset, 0 3px 12px -5px rgba(245,158,11,0.5);
}
.lp-credits-chip b { font-weight: 700; }
.lp-credits-chip:hover:not(:disabled) { border-color: rgba(245,158,11,0.8); }

/* ───── Train action bar (Type / Video / Attach / Skip) ────────────────────
   Must never be wider than the viewport: an oversized bar blows out the
   stage's grid column and shoves the whole screen off-center on phones.
   max-width + wrap keeps it inside the column; the media query tightens
   the buttons so all four actions fit one row on typical phone widths. */
.lp-action-bar {
  max-width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px;
  border-radius: 2px 16px 2px 16px;
  background: rgba(255,255,255,0.7);
  border: 1px solid var(--lp-line);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 1px 0 rgba(255,255,255,0.8) inset, 0 6px 18px -8px rgba(40,50,40,0.12);
}
@media (max-width: 520px) {
  .lp-action-bar { gap: 2px; padding: 4px; }
  .lp-action-bar .lp-btn {
    padding: 10px 9px;
    gap: 5px;
    font-size: 10px;
    letter-spacing: 0.1em;
  }
}

/* ───── Orb answer-mode toggle (Voice / Video) ─────────────────────────────
   Floating chip pair under the orb that pre-selects what a tap on the orb
   does. Deliberately NOT the toolbar's frosted-bar chrome: the active side
   is filled with the same gradient as the orb's ring, tying it to the
   respond button rather than the action bar below it. */
.lp-mode-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.lp-mode-toggle-opt {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 2px 12px 2px 12px;
  border: 1px dashed rgba(40,50,40,0.28);
  background: transparent;
  color: var(--lp-ink-soft);
  font-family: var(--lp-mono);
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 700;
  cursor: pointer;
  transition: background 180ms ease, color 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
}
.lp-mode-toggle-opt svg { flex: none; }
.lp-mode-toggle-opt[aria-checked='true'] {
  background: linear-gradient(135deg, var(--lp-violet) 0%, var(--lp-pink) 50%, var(--lp-orange) 100%);
  border: 1px solid transparent;
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(236,72,153,0.55), 0 1px 0 rgba(255,255,255,0.25) inset;
}
.lp-mode-toggle-opt:hover:not([aria-checked='true']) { color: var(--lp-ink); border-style: solid; }

/* ───── Fun & Games ───────────────────────────────────────────────────────
   While a game is on, its card takes the host avatar's row (about the same
   height) and the ways to answer sit under the interviewer's message. Those
   share the message's row, so every pixel they take comes out of the room the
   message is fitted to: compact by design, and long lists scroll in place
   rather than pushing the orb down. */
.lp-game-card {
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px 18px 10px;
  border-radius: 2px 16px 2px 16px;
  background: rgba(255,255,255,0.7);
  border: 1px solid var(--lp-line);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 1px 0 rgba(255,255,255,0.8) inset, 0 6px 18px -8px rgba(40,50,40,0.12);
  text-align: center;
  animation: lp-fade-in 320ms ease both;
}
.lp-game-eyebrow { color: var(--lp-pink); letter-spacing: 0.16em; }
/* Time Machine: "1978 · age 12". */
.lp-game-era {
  font-family: var(--lp-serif);
  font-weight: 300;
  font-size: clamp(22px, 3.6vh, 32px);
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: var(--lp-ink);
}
.lp-game-title {
  font-family: var(--lp-serif);
  font-style: italic;
  font-size: 16px;
  line-height: 1.25;
  color: var(--lp-ink-deep);
  text-wrap: balance;
}
/* Story Dice: the rolled words as two paper tiles, tipped like thrown dice. */
.lp-game-dice { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px; padding: 2px 0; }
.lp-game-die {
  min-width: 72px;
  padding: 6px 14px;
  border-radius: 4px 14px 4px 14px;
  background: var(--lp-paper-warm);
  border: 1px solid var(--lp-line-strong);
  box-shadow: 0 1px 0 rgba(255,255,255,0.9) inset, 0 6px 14px -8px rgba(40,50,40,0.28);
  font-family: var(--lp-serif);
  font-size: 17px;
  line-height: 1.1;
  color: var(--lp-ink);
  transform: rotate(-3deg);
}
.lp-game-die:nth-child(even) { transform: rotate(2.5deg); }

.lp-game-answers {
  flex: none;
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  animation: lp-fade-in 320ms ease both;
}
.lp-game-choices {
  width: 100%;
  max-height: 104px;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}
/* Choices are what the person would have said, so they read as words (serif,
   sentence case), not as the toolbar's uppercase commands. */
.lp-game-choice {
  flex: 1 1 130px;
  min-height: 44px;
  padding: 8px 16px;
  font-family: var(--lp-serif);
  font-size: 17px;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
  line-height: 1.2;
}
/* "Add a photo" (primary when a game wants one) and the game's own buttons. */
.lp-game-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 18px;
}
.lp-game-results {
  width: 100%;
  max-height: 104px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 6px;
  text-align: left;
}
.lp-game-results li {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--lp-line);
}
.lp-game-winner {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  font-family: var(--lp-serif);
  font-size: 16px;
  color: var(--lp-ink);
}
.lp-game-votes {
  font-family: var(--lp-mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  color: var(--lp-ink-mute);
  white-space: nowrap;
}
/* The finishing bonus: the credits chip, a size up, popping in like the top
   bar's "+N". */
.lp-game-bonus {
  height: 36px;
  padding: 0 16px;
  font-size: 12px;
  letter-spacing: 0.14em;
  cursor: pointer;
  animation: lp-credits-pop 640ms cubic-bezier(.22,.61,.36,1) both;
}
@media (prefers-reduced-motion: reduce) {
  .lp-game-card, .lp-game-answers, .lp-game-bonus { animation: none; }
}

/* ───── Inputs ─────────────────────────────────────────────────────────── */
.lp-field-label {
  display: block;
  font-family: var(--lp-mono);
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 600;
  color: var(--lp-ink-soft);
  margin-bottom: 6px;
  text-align: left;
}
.lp-input,
.lp-textarea {
  width: 100%;
  background: rgba(255,255,255,0.85);
  border: 1px solid var(--lp-line-strong);
  border-radius: 2px 14px 2px 14px;
  padding: 11px 14px;
  font-family: var(--lp-serif);
  font-size: 15px;
  color: var(--lp-ink);
  transition: border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
  box-shadow: 0 1px 0 rgba(255,255,255,0.7) inset;
}
.lp-input::placeholder,
.lp-textarea::placeholder { color: rgba(40,50,40,0.40); }
.lp-input:focus,
.lp-textarea:focus {
  outline: none;
  border-color: var(--lp-pink);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(236,72,153,0.18);
}
.lp-input:disabled,
.lp-textarea:disabled { opacity: 0.6; cursor: not-allowed; }
.lp-textarea { resize: vertical; min-height: 88px; font-family: var(--lp-serif); }

.lp-input-affix {
  position: relative;
}
.lp-input-affix .lp-input { padding-right: 44px; }
.lp-input-affix-end {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--lp-ink-mute);
  background: transparent;
  border: 0;
  padding: 4px;
  cursor: pointer;
}
.lp-input-affix-end:hover { color: var(--lp-ink); }

/* Radio / checkbox tile */
.lp-tile {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 2px 14px 2px 14px;
  border: 1px solid var(--lp-line-strong);
  background: rgba(255,255,255,0.6);
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease, box-shadow 160ms ease;
}
.lp-tile:hover { background: rgba(255,255,255,0.85); }
.lp-tile[data-selected="true"] {
  background: rgba(255,255,255,0.95);
  border-color: var(--lp-pink);
  box-shadow: 0 0 0 2px rgba(236,72,153,0.18);
}
.lp-tile-text { color: var(--lp-ink); font-family: var(--lp-serif); font-size: 14px; line-height: 1.35; }
.lp-tile-hint { display: block; color: var(--lp-ink-mute); font-family: var(--lp-mono); font-size: 11px; margin-top: 2px; letter-spacing: 0.04em; text-transform: none; }

/* Segmented row used for compact channel selection */
.lp-segmented {
  display: inline-flex;
  gap: 6px;
  padding: 4px;
  border-radius: 2px 14px 2px 14px;
  background: rgba(255,255,255,0.6);
  border: 1px solid var(--lp-line);
}
.lp-segmented button {
  flex: 1;
  border: 0;
  background: transparent;
  padding: 8px 10px;
  border-radius: 2px 10px 2px 10px;
  font-family: var(--lp-mono);
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--lp-ink-soft);
  cursor: pointer;
  transition: background 160ms ease, color 160ms ease;
}
.lp-segmented button[aria-pressed="true"] {
  background:
    linear-gradient(rgba(255,255,255,0.94), rgba(250,249,243,0.94)) padding-box,
    conic-gradient(from 120deg, var(--lp-violet), var(--lp-pink), var(--lp-orange), var(--lp-amber), var(--lp-violet)) border-box;
  color: var(--lp-ink);
  border: 2px solid transparent;
  box-shadow:
    0 1px 0 rgba(255,255,255,0.85) inset,
    0 0 0 1px rgba(236,72,153,0.08),
    0 6px 14px -8px rgba(236,72,153,0.32);
}
.lp-segmented button:disabled { opacity: 0.4; cursor: not-allowed; }

/* Alert / error notice */
.lp-error {
  border: 1px solid rgba(220, 38, 38, 0.30);
  background: rgba(254, 226, 226, 0.55);
  color: #7f1d1d;
  font-family: var(--lp-mono);
  font-size: 12px;
  border-radius: 2px 14px 2px 14px;
  padding: 10px 14px;
  letter-spacing: 0.02em;
}

/* Loader */
.lp-spinner {
  width: 48px; height: 48px;
  border-radius: 50%;
  border: 3px solid rgba(40,50,40,0.10);
  border-top-color: var(--lp-pink);
  border-right-color: var(--lp-violet);
  animation: lp-spin 0.9s linear infinite;
}
.lp-spinner-sm {
  width: 18px; height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(40,50,40,0.15);
  border-top-color: currentColor;
  animation: lp-spin 0.9s linear infinite;
  display: inline-block;
}

/* Backdrop */
.lp-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(238, 239, 232, 0.65);
  backdrop-filter: blur(14px) saturate(1.1);
  -webkit-backdrop-filter: blur(14px) saturate(1.1);
  z-index: 1100;
  animation: lp-fade-in 200ms ease;
}

/* Modal centering wrapper */
.lp-modal-wrap {
  position: fixed;
  inset: 0;
  z-index: 1150;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
@media (min-width: 640px) {
  .lp-modal-wrap { align-items: center; padding: 24px; }
}

.lp-modal-card {
  position: relative;
  width: 100%;
  max-width: 520px;
  margin: 0;
  background: var(--lp-paper-2);
  border: 1px solid var(--lp-line);
  border-radius: 0;
  box-shadow:
    0 1px 0 rgba(255,255,255,0.85) inset,
    0 24px 60px -16px rgba(40,50,40,0.28),
    0 8px 24px -10px rgba(40,50,40,0.12);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  animation: lp-fade-in 280ms ease;
}
@media (min-width: 640px) {
  .lp-modal-card {
    min-height: 0;
    max-height: 90dvh;
    border-radius: 2px 22px 2px 22px;
  }
}

.lp-modal-body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: calc(env(safe-area-inset-top, 0px) + 32px) 24px calc(env(safe-area-inset-bottom, 0px) + 28px);
}
@media (min-width: 640px) {
  .lp-modal-body { padding: 36px 36px 32px; }
}

.lp-modal-close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
}

.lp-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 16px 0;
}
.lp-divider::before,
.lp-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--lp-line);
}
.lp-divider span {
  font-family: var(--lp-mono);
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--lp-ink-mute);
}

.lp-link {
  color: var(--lp-ink);
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color 160ms ease;
}
.lp-link:hover { color: var(--lp-pink); }

.lp-footer-link {
  font-family: var(--lp-mono);
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--lp-ink-mute);
  text-align: center;
}
.lp-footer-link a { color: var(--lp-ink); }

.lp-section-heading {
  font-family: var(--lp-serif);
  font-weight: 320;
  font-size: clamp(22px, 4vw, 30px);
  letter-spacing: -0.014em;
  line-height: 1.15;
  color: var(--lp-ink);
  margin: 0;
  text-wrap: balance;
}
.lp-section-sub {
  font-family: var(--lp-mono);
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--lp-ink-mute);
}
.lp-prose {
  font-family: var(--lp-serif);
  color: var(--lp-ink-soft);
  font-size: 15px;
  line-height: 1.5;
  text-wrap: pretty;
}

/* Logo mark with paper disc */
.lp-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background:
    radial-gradient(120% 120% at 30% 20%, rgba(255,255,255,0.95), rgba(255,255,255,0.55) 50%, rgba(238,239,232,0.0) 80%),
    linear-gradient(160deg, #faf9f3 0%, #ecede4 100%);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.8),
    inset 0 -14px 24px rgba(40,50,40,0.08),
    0 6px 18px -8px rgba(40,50,40,0.18);
}
.lp-mark::after {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  padding: 2px;
  background: conic-gradient(from 120deg, var(--lp-violet), var(--lp-pink), var(--lp-orange), var(--lp-amber), var(--lp-violet));
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask-composite: exclude;
  opacity: 0.55;
}
.lp-mark img { width: 60%; height: 60%; opacity: 0.92; }

/* Modal overrides for shared TrainScreen partials still imported by the
   light variant (Modal, AttachmentModal, TopModal, ActionsPopover). Scoped to
   body.lp-active so other contexts aren't affected. */
body.lp-active .bg-black\\/20,
body.lp-active .bg-black\\/60,
body.lp-active .bg-black\\/70 {
  background-color: rgba(238, 239, 232, 0.55) !important;
  backdrop-filter: blur(14px) saturate(1.1);
  -webkit-backdrop-filter: blur(14px) saturate(1.1);
}

body.lp-active .modal-container > div {
  background-color: var(--lp-paper-2) !important;
  border: 1px solid rgba(40, 50, 40, 0.10) !important;
  border-radius: 2px 22px 2px 22px !important;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 18px 50px -12px rgba(40, 50, 40, 0.25),
    0 6px 20px -8px rgba(40, 50, 40, 0.12) !important;
  color: var(--lp-ink) !important;
}
body.lp-active .modal-container h5,
body.lp-active .modal-container h5 svg { color: var(--lp-ink) !important; }
body.lp-active .modal-container .h-px { background-color: rgba(40, 50, 40, 0.10) !important; }
body.lp-active .modal-container textarea {
  background-color: rgba(255, 255, 255, 0.7) !important;
  border-color: rgba(40, 50, 40, 0.16) !important;
  border-radius: 2px 14px 2px 14px !important;
  color: var(--lp-ink) !important;
  font-family: var(--lp-serif) !important;
}
body.lp-active .modal-container textarea::placeholder { color: rgba(40, 50, 40, 0.45) !important; }
body.lp-active .modal-container button {
  border: 1px solid rgba(40, 50, 40, 0.18) !important;
  border-radius: 2px 14px 2px 14px !important;
  color: var(--lp-ink) !important;
  background:
    linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)) padding-box !important;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  transition: background 200ms ease, color 200ms ease, border-color 200ms ease, transform 200ms ease !important;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 4px 12px -4px rgba(40, 50, 40, 0.12) !important;
}
body.lp-active .modal-container button:hover {
  background:
    linear-gradient(rgba(255, 255, 255, 0.96), rgba(255, 255, 255, 0.96)) padding-box,
    linear-gradient(135deg, var(--lp-violet) 0%, var(--lp-pink) 50%, var(--lp-orange) 100%) border-box !important;
  border: 1px solid transparent !important;
}
body.lp-active div[style*="background-color: rgb(61, 61, 61)"],
body.lp-active div[style*="background-color: #3D3D3D"],
body.lp-active div[style*="background-color:#3D3D3D"] {
  background-color: var(--lp-paper-2) !important;
  color: var(--lp-ink) !important;
  border: 1px solid rgba(40, 50, 40, 0.10) !important;
  border-radius: 2px 18px 2px 18px !important;
}
body.lp-active div[style*="background-color: rgb(61, 61, 61)"] .text-white,
body.lp-active div[style*="background-color: #3D3D3D"] .text-white,
body.lp-active div[style*="background-color:#3D3D3D"] .text-white { color: var(--lp-ink) !important; }
body.lp-active .bg-white.rounded-2xl.max-w-lg {
  background-color: var(--lp-paper-2) !important;
  border: 1px solid rgba(40, 50, 40, 0.10) !important;
  border-radius: 2px 22px 2px 22px !important;
}
body.lp-active .bg-white.rounded-2xl.max-w-lg .text-gray-900 { color: var(--lp-ink) !important; }
body.lp-active .bg-white.rounded-2xl.max-w-lg .border-gray-200 { border-color: rgba(40, 50, 40, 0.10) !important; }
body.lp-active .bg-white.rounded-2xl.max-w-lg .text-gray-500,
body.lp-active .bg-white.rounded-2xl.max-w-lg .text-gray-400,
body.lp-active .bg-white.rounded-2xl.max-w-lg .text-gray-600 { color: var(--lp-ink-soft) !important; }
body.lp-active .bg-white.rounded-2xl.max-w-lg [class*="text-primary"],
body.lp-active .bg-white.rounded-2xl.max-w-lg [class*="text-yellow"] { color: var(--lp-pink) !important; }
body.lp-active .bg-white.rounded-2xl.max-w-lg [class*="bg-primary"] { background-color: rgba(236, 72, 153, 0.08) !important; }
body.lp-active .bg-white.rounded-2xl.max-w-lg [class*="border-primary"] { border-color: rgba(236, 72, 153, 0.30) !important; }
body.lp-active .bg-white.rounded-2xl.max-w-lg [class*="bg-yellow-50"] { background-color: rgba(236, 72, 153, 0.06) !important; }
body.lp-active .bg-white.rounded-2xl.max-w-lg [class*="border-yellow"] { border-color: rgba(236, 72, 153, 0.30) !important; }

/* The legacy AttachmentModal ships with z-[1000], which slides under the
   LightReplyModal (z-9999) when launched from the bottom sheet's Attach
   button. Bump it above so the user can pick attachments without the reply
   sheet covering it. */
body.lp-active .fixed.inset-0.bg-black\\/70 { z-index: 10000 !important; }

/* Hide scrollbars but keep scroll behaviour. Used on the question section so
   the long-text-safety overflow:auto doesn't render a visible scroll track
   while Roxy is mid-utterance. */
.lp-hide-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
.lp-hide-scrollbar::-webkit-scrollbar { width: 0; height: 0; display: none; }
    `}</style>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Primitive components
// ─────────────────────────────────────────────────────────────────────────────

interface LightShellProps {
  children: React.ReactNode;
  scroll?: boolean;
  className?: string;
}

/** Page shell with grain + drifting rings. Use scroll for screens whose content
 *  may overflow (auth screens, onboarding); leave it off for fixed in-canvas
 *  experiences like the orb. */
export function LightShell({ children, scroll = false, className }: LightShellProps) {
  return (
    <div className={cx(scroll ? 'lp-scroll-root' : 'lp-root', className)}>
      <div className="lp-grain" aria-hidden />
      <div className="lp-rings" aria-hidden />
      <div style={{ position: 'relative', zIndex: 1, minHeight: '100%' }}>{children}</div>
    </div>
  );
}

interface LightModalProps {
  open: boolean;
  onClose?: () => void;
  /** When true, clicking the backdrop closes the modal. */
  dismissable?: boolean;
  ariaLabel: string;
  children: React.ReactNode;
  width?: number;
}

export function LightModal({ open, onClose, dismissable = true, ariaLabel, children, width }: LightModalProps) {
  if (!open) return null;
  const maxWidth = width ? `${width}px` : undefined;
  return (
    <>
      <div
        className="lp-backdrop"
        aria-hidden
        onClick={dismissable && onClose ? onClose : undefined}
      />
      <div className="lp-modal-wrap" role="dialog" aria-modal="true" aria-label={ariaLabel}>
        <div className="lp-modal-card" style={maxWidth ? { maxWidth } : undefined}>
          {children}
        </div>
      </div>
    </>
  );
}

export function LightCloseButton({ onClick, label = 'Close' }: { onClick: () => void; label?: string }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} className="lp-icon-btn lp-modal-close">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="6" y1="6" x2="18" y2="18" />
        <line x1="18" y1="6" x2="6" y2="18" />
      </svg>
    </button>
  );
}

interface LightMarkProps {
  src: string;
  alt?: string;
  /** Outer paper-disc diameter in pixels. */
  size?: number;
  /** Fraction of the disc that the inner image fills (0–1). Defaults to the
   *  CSS-defined 60%. Override per-screen to give a wordmark or tighter glyph
   *  more breathing room. */
  imgScale?: number;
}
export function LightMark({ src, alt = 'Proxi', size = 72, imgScale }: LightMarkProps) {
  const imgStyle =
    imgScale !== undefined
      ? { width: `${imgScale * 100}%`, height: `${imgScale * 100}%` }
      : undefined;
  return (
    <span className="lp-mark" style={{ width: size, height: size }}>
      <img src={src} alt={alt} style={imgStyle} />
    </span>
  );
}

interface LightSpinnerProps { size?: number }
export function LightSpinner({ size = 48 }: LightSpinnerProps) {
  return (
    <span
      className="lp-spinner"
      style={{ width: size, height: size }}
      role="status"
      aria-label="Loading"
    />
  );
}

interface FitLineProps {
  children: React.ReactNode;
  /** Styles for the line box (font, color, margins). Layout props that would
   *  break the fit measurement (white-space, overflow) are owned here. */
  style?: React.CSSProperties;
}

/** Renders its children on a single line, scaled down just enough to fit the
 *  container's width — for text that must never wrap or truncate (the auth
 *  screens' legal line). Uses transform: scale, which doesn't affect layout,
 *  so the measurement never feeds back into itself. Re-fits on container
 *  resize and once webfonts finish loading (JetBrains Mono is wider than the
 *  fallback, so an early measurement would under-shrink). */
export function FitLine({ children, style }: FitLineProps) {
  const outerRef = React.useRef<HTMLDivElement | null>(null);
  const innerRef = React.useRef<HTMLSpanElement | null>(null);
  const [scale, setScale] = React.useState(1);

  React.useLayoutEffect(() => {
    const fit = () => {
      const outer = outerRef.current;
      const inner = innerRef.current;
      if (!outer || !inner) return;
      const available = outer.clientWidth;
      const natural = inner.scrollWidth;
      // 0.5px slack absorbs scrollWidth's integer rounding so the tail of
      // the line is never clipped by a fraction of a pixel.
      setScale(natural > available && natural > 0 ? Math.max(0, available - 0.5) / natural : 1);
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (ro && outerRef.current) ro.observe(outerRef.current);
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(fit).catch(() => undefined);
    }
    return () => ro?.disconnect();
  }, []);

  return (
    <div
      ref={outerRef}
      style={{ ...style, whiteSpace: 'nowrap', overflow: 'hidden' }}
    >
      {/* max-content block: auto margins center it while it fits; once it
          overflows they collapse to 0 and it sits flush left, so scaling
          from the LEFT edge makes the shrunk line span the container
          exactly. (An inline-block + center origin drifts right instead,
          because the browser left-aligns overflowing lines.) */}
      <span
        ref={innerRef}
        style={{
          display: 'block',
          width: 'max-content',
          margin: '0 auto',
          transform: `scale(${scale})`,
          transformOrigin: 'left center',
        }}
      >
        {children}
      </span>
    </div>
  );
}
